import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';

type ScriptAttrs = {
  src: string;
  type?: string;
  crossorigin?: string;
  async?: boolean;
  defer?: boolean;
  id?: string;
};

const DATA_LOADED_ATTR = 'data-loaded';

const defaultAttrs: Partial<ScriptAttrs> = {
  type: 'text/javascript',
};

export const createScriptElement = (attrs: ScriptAttrs, id?: string): HTMLScriptElement => {
  const scriptEl = document.createElement('script');

  if (id) {
    scriptEl.id = id;
  }

  Object.entries(attrs).forEach(([key, value]) => {
    if (key === 'async' || key === 'defer') {
      if (value) {
        scriptEl[key] = true;
      }
    } else {
      scriptEl.setAttribute(key, String(value));
    }
  });

  return scriptEl;
};

export const loadScript = async (attrs: ScriptAttrs, id?: string): Promise<boolean> => {
  if (!attrs?.src) {
    throw new Error('Missing script src');
  }

  const scriptEl = createScriptElement(
    omitBy(
      {
        ...defaultAttrs,
        ...attrs,
      },
      isUndefined,
    ) as ScriptAttrs,
    id,
  );

  return new Promise<boolean>((resolve, reject) => {
    scriptEl.addEventListener('load', () => {
      scriptEl.setAttribute(DATA_LOADED_ATTR, 'true');
      resolve(true);
    });
    scriptEl.addEventListener('error', () => reject(new Error(`Failed to load script ${attrs.src}`)));

    document.head.appendChild(scriptEl);
  });
};

export const isScriptLoadingCompleted = (scriptEl: HTMLElement): Promise<boolean> =>
  new Promise((resolve) => {
    const dataLoadedAttribute = scriptEl.getAttribute(DATA_LOADED_ATTR);

    if (dataLoadedAttribute === 'true') {
      return resolve(true);
    }

    scriptEl.addEventListener('load', () => resolve(true), { once: true });
    scriptEl.addEventListener('error', () => resolve(false), { once: true });
  });
